import { useForm } from "@inertiajs/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { bizminer_admin_organization_credits_path } from "@/routes";

interface Props {
  open: boolean;
  onClose: () => void;
  organizationId: number;
  organizationName: string;
  currentBalance: number;
}

const GRANT_CATEGORIES = [
  { value: "manual_grant", label: "Manual Grant" },
  { value: "bonus", label: "Bonus" },
  { value: "admin_adjustment", label: "Admin Adjustment" },
  { value: "referral_bonus", label: "Referral Bonus" },
];

// File-local components

interface AmountFieldProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

const AmountField = ({ value, onChange, error }: AmountFieldProps) => (
  <div>
    <Label htmlFor="amount">Amount *</Label>
    <Input
      id="amount"
      type="number"
      min="1"
      placeholder="Enter credit amount"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1"
    />
    {error && <p className="text-sm text-destructive mt-1">{error}</p>}
  </div>
);

interface CategoryFieldProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

const CategoryField = ({
  value,
  onChange,
  error,
}: CategoryFieldProps) => (
  <div>
    <Label htmlFor="category">Category *</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {GRANT_CATEGORIES.map((cat) => (
          <SelectItem key={cat.value} value={cat.value}>
            {cat.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    {error && <p className="text-sm text-destructive mt-1">{error}</p>}
  </div>
);

interface ReasonFieldProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

const ReasonField = ({ value, onChange, error }: ReasonFieldProps) => (
  <div>
    <Label htmlFor="reason">Reason / Notes *</Label>
    <Textarea
      id="reason"
      placeholder="e.g., December 2025 contract fulfillment"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1"
      rows={3}
      maxLength={500}
    />
    <p className="text-xs text-muted-foreground mt-1">
      {value.length}/500 characters
    </p>
    {error && <p className="text-sm text-destructive mt-1">{error}</p>}
  </div>
);

interface PreviewSectionProps {
  currentBalance: number;
  grantAmount: number;
}

const PreviewSection = ({
  currentBalance,
  grantAmount,
}: PreviewSectionProps) => {
  const newBalance = currentBalance + grantAmount;

  return (
    <div className="border-t pt-4">
      <h4 className="text-sm font-medium text-foreground mb-2">Preview</h4>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Current Balance:</span>
          <span className="font-medium text-foreground">
            {currentBalance.toLocaleString()} credits
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Grant Amount:</span>
          <span className="font-medium text-chart-6">
            +{grantAmount.toLocaleString()} credits
          </span>
        </div>
        <div className="flex justify-between border-t pt-1">
          <span className="font-medium text-foreground">New Balance:</span>
          <span className="font-bold text-foreground">
            {newBalance.toLocaleString()} credits
          </span>
        </div>
      </div>
    </div>
  );
};

interface FormActionsProps {
  onCancel: () => void;
  processing: boolean;
  grantAmount: number;
}

const FormActions = ({
  onCancel,
  processing,
  grantAmount,
}: FormActionsProps) => (
  <div className="flex justify-end gap-3 pt-2">
    <Button type="button" variant="outline" onClick={onCancel}>
      Cancel
    </Button>
    <Button type="submit" disabled={processing}>
      {processing ? "Granting..." : `Grant ${grantAmount || 0} Credits`}
    </Button>
  </div>
);

// Main component

export default function GrantCreditsModal({
  open,
  onClose,
  organizationId,
  organizationName,
  currentBalance,
}: Props) {
  const { data, setData, post, processing, errors, reset } = useForm({
    amount: "",
    category: "manual_grant",
    reason: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    post(bizminer_admin_organization_credits_path(organizationId), {
      preserveScroll: true,
      onSuccess: () => {
        reset();
        onClose();
      },
    });
  };

  const grantAmount = parseInt(data.amount) || 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] text-foreground">
        <DialogHeader>
          <DialogTitle>Grant Credits to {organizationName}</DialogTitle>
          <DialogDescription>
            Add credits to this organization's balance
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <AmountField
            value={data.amount}
            onChange={(value) => setData("amount", value)}
            error={errors.amount}
          />

          <CategoryField
            value={data.category}
            onChange={(value) => setData("category", value)}
            error={errors.category}
          />

          <ReasonField
            value={data.reason}
            onChange={(value) => setData("reason", value)}
            error={errors.reason}
          />

          <PreviewSection
            currentBalance={currentBalance}
            grantAmount={grantAmount}
          />

          <FormActions
            onCancel={onClose}
            processing={processing}
            grantAmount={grantAmount}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}
